import { createClient as createAuthClient } from "../../supabase/client"
import { createDataClient } from "../../supabase/dataClient"

import type {
  BuildReadClient,
  BuildRow,
  BuildRowsQueryResult,
  DataProvider,
  PublishedBuildsParams,
} from "./types"

type AuthorRow = {
  id: string
  username: string | null
  full_name: string | null
  avatar_url: string | null
}

function pickAuthorName(row: AuthorRow | undefined): string | null {
  if (!row) return null
  return row.full_name?.trim() || row.username?.trim() || null
}

async function fetchAuthorMap(
  // The narrow BuildReadClient interface only exposes from("builds"); the real
  // browser client is a full SupabaseClient, so we cast to reach build_authors.
  supabase: unknown,
  userIds: string[]
): Promise<Map<string, AuthorRow>> {
  const ids = Array.from(new Set(userIds.filter((v): v is string => Boolean(v))))
  if (ids.length === 0) return new Map()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = supabase as any
  const { data, error } = await client
    .from("build_authors")
    .select("id, username, full_name, avatar_url")
    .in("id", ids)

  if (error) {
    // Non-fatal: if the view isn't present in this env, fall back to empty.
    console.warn("[data-provider] build_authors fetch failed:", error.message)
    return new Map()
  }

  const map = new Map<string, AuthorRow>()
  for (const row of (data ?? []) as AuthorRow[]) {
    if (row?.id) map.set(row.id, row)
  }
  return map
}

function attachAuthorNames(
  rows: BuildRow[],
  authors: Map<string, AuthorRow>
): BuildRow[] {
  return rows.map((row) => {
    const author = row.user_id ? authors.get(row.user_id) : undefined
    const authorName = pickAuthorName(author)
    return authorName == null
      ? row
      : ({ ...row, author_name: authorName } as BuildRow)
  })
}

type CreateClientFactory = () => BuildReadClient
type CreateAuthClientFactory = () => Pick<BuildReadClient, "auth">

type CreateSupabaseDataProviderOptions = {
  createClientImpl?: CreateClientFactory
  createAuthClientImpl?: CreateAuthClientFactory
}

function clampPage(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback
  return Math.max(1, Math.floor(value))
}

function clampPageSize(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback
  return Math.min(Math.max(1, Math.floor(value)), 100)
}

export function createSupabaseDataProvider({
  createClientImpl = createDataClient as unknown as CreateClientFactory,
  createAuthClientImpl = createAuthClient as unknown as CreateAuthClientFactory,
}: CreateSupabaseDataProviderOptions = {}): DataProvider {
  function getDataClient() {
    return createClientImpl()
  }

  function getAuthClient() {
    return createAuthClientImpl()
  }

  return {
    publicBuilds: {
      async listPublishedBuilds(
        params: PublishedBuildsParams = {}
      ): Promise<{ items: BuildRow[]; total: number }> {
        const supabase = getDataClient()
        const page = clampPage(params.page, 1)
        const pageSize = clampPageSize(params.pageSize, 30)
        const offset = (page - 1) * pageSize

        let query = supabase
          .from("builds")
          .select("*", { count: "exact" })
          .eq("status", "published")

        if (params.tag) {
          query = query.contains("tags", [params.tag])
        }

        if (params.sort === "stars") {
          query = query.order("stars", { ascending: false })
        } else if (params.sort === "forks") {
          query = query.order("fork_count", { ascending: false })
        } else if (params.sort === "installs") {
          query = query.order("install_count", { ascending: false })
        } else {
          query = query.order("created_at", { ascending: false })
        }

        query = query.range(offset, offset + pageSize - 1)

        const { data, count, error } = (await query) as BuildRowsQueryResult<BuildRow>
        if (error) throw new Error(`Failed to fetch builds: ${error.message}`)

        const rows = data ?? []
        const authors = await fetchAuthorMap(
          supabase,
          rows.map((row) => row.user_id).filter((v): v is string => Boolean(v))
        )

        return { items: attachAuthorNames(rows, authors), total: count ?? 0 }
      },
      async getPublishedBuildBySlug(slug: string): Promise<BuildRow | null> {
        const supabase = getDataClient()
        const { data, error } = await supabase
          .from("builds")
          .select("*")
          .eq("slug", slug)
          .maybeSingle()

        if (error) throw new Error(`Failed to fetch build: ${error.message}`)
        if (!data) return null

        const authors = data.user_id
          ? await fetchAuthorMap(supabase, [data.user_id])
          : new Map<string, AuthorRow>()
        return attachAuthorNames([data], authors)[0]
      },
    },
    viewerBuilds: {
      async getViewerBuildById(buildId: string): Promise<BuildRow | null> {
        const supabase = getDataClient()
        const { data, error } = await supabase
          .from("builds")
          .select("*")
          .eq("id", buildId)
          .single()

        if (error) {
          if (error.code === "PGRST116") return null
          throw new Error(`Failed to fetch build: ${error.message}`)
        }

        return data
      },
      async listViewerBuilds(): Promise<BuildRow[]> {
        const authClient = getAuthClient()
        const {
          data: { user },
        } = await authClient.auth.getUser()

        if (!user) return []

        const supabase = getDataClient()
        const { data, error } = (await supabase
          .from("builds")
          .select("*")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })) as BuildRowsQueryResult<BuildRow>

        if (error) {
          throw new Error(`Failed to fetch your builds: ${error.message}`)
        }
        return data ?? []
      },
    },
  }
}

export function getDataProvider(
  options?: CreateSupabaseDataProviderOptions
): DataProvider {
  return createSupabaseDataProvider(options)
}
